import type { ReactNode } from 'react';
import { Button, Card, EmptyState, ErrorState, IconGlyph, LoadingState, Sheet } from '../primitives/index.js';

export function MainTabScreen(props: { title: string; eyebrow?: string; children: ReactNode }) {
  return <ScreenFrame title={props.title} eyebrow={props.eyebrow} kind="main">{props.children}</ScreenFrame>;
}

export function DetailScreen(props: { title: string; backHref?: string; children: ReactNode }) {
  return (
    <ScreenFrame title={props.title} kind="detail">
      {props.backHref ? <a className="mira-detail-back" href={props.backHref}>Back</a> : null}
      {props.children}
    </ScreenFrame>
  );
}

export function FormScreen(props: { title: string; children: ReactNode }) {
  return <ScreenFrame title={props.title} kind="form">{props.children}</ScreenFrame>;
}

export function ChatScreenFrame(props: { title: string; children: ReactNode; backHref: string }) {
  return (
    <ScreenFrame title={props.title} kind="chat">
      <a className="mira-detail-back" href={props.backHref}>Back</a>
      {props.children}
    </ScreenFrame>
  );
}

export function Section(props: { title: string; children: ReactNode }) {
  return (
    <section className="mira-section">
      <h2>{props.title}</h2>
      {props.children}
    </section>
  );
}

export function ListState(props: {
  state: 'loading' | 'empty' | 'error' | 'ready';
  children: ReactNode;
  emptyActionHref?: string;
  emptyActionLabel?: string;
  emptyBody?: string;
  emptyTitle?: string;
  errorActionHref?: string;
  errorActionLabel?: string;
  errorBody?: string;
  errorTitle?: string;
  loadingBody?: string;
  loadingTitle?: string;
}) {
  if (props.state === 'loading') return <LoadingState title={props.loadingTitle} body={props.loadingBody} />;
  if (props.state === 'empty') return <EmptyState title={props.emptyTitle || 'Nothing here yet'} body={props.emptyBody} actionHref={props.emptyActionHref} actionLabel={props.emptyActionLabel} />;
  if (props.state === 'error') return <ErrorState title={props.errorTitle || 'Could not load'} body={props.errorBody} actionHref={props.errorActionHref} actionLabel={props.errorActionLabel} />;
  return <>{props.children}</>;
}

export function DetailTopbar(props: { backHref: string; backLabel?: string; label?: string; menuLabel?: string; onMenu?: () => void }) {
  return (
    <div className="mira-detail-topbar">
      <a href={props.backHref} aria-label="Back">←<span>{props.backLabel || 'Back'}</span></a>
      {props.label ? <strong>{props.label}</strong> : <span />}
      {props.onMenu ? <button aria-label={props.menuLabel || 'More'} onClick={props.onMenu} type="button">…</button> : <span />}
    </div>
  );
}

export function DangerDialog(props: { body: ReactNode; busy?: boolean; compact?: boolean; confirmLabel: string; confirmLoadingLabel?: string; onCancel: () => void; onConfirm: () => void; preview?: ReactNode; stackActions?: boolean; title: string }) {
  return (
    <Sheet className={props.stackActions ? 'mira-danger-sheet-stack' : props.compact ? 'mira-danger-sheet-compact' : undefined} title={props.title}>
      <div className="mira-danger-dialog" data-actions={props.stackActions ? 'stack' : undefined} data-compact={props.compact ? 'true' : undefined}>
        <span className="mira-danger-icon" aria-hidden="true"><IconGlyph value="⌫" /></span>
        <h2>{props.title}</h2>
        <div className="mira-danger-body">{props.body}</div>
        {props.preview}
        <div className="mira-danger-actions">
          <Button disabled={props.busy} tone="secondary" onClick={props.onCancel}>Cancel</Button>
          <Button busy={props.busy} disabled={props.busy} tone="danger" onClick={props.onConfirm}>{props.busy ? props.confirmLoadingLabel || 'Working…' : props.confirmLabel}</Button>
        </div>
      </div>
    </Sheet>
  );
}

export function ConfirmSheet(props: {
  title: string;
  body: string;
  busy?: boolean;
  className?: string;
  confirmLabel: string;
  error?: string;
  icon?: string;
  preview?: ReactNode;
  success?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Sheet className={props.className ? `mira-confirm-sheet ${props.className}` : 'mira-confirm-sheet'} title={props.title}>
      <div className="mira-danger-dialog">
        <span className="mira-danger-icon" aria-hidden="true"><IconGlyph value={props.icon || '⌫'} /></span>
        <h2>{props.title}</h2>
        <p>{props.body}</p>
        {props.preview}
        {props.error ? <p className="mira-form-error">{props.error}</p> : null}
        {props.success ? <p className="mira-soft-note">{props.success}</p> : null}
        <div className="mira-danger-actions">
          <Button disabled={props.busy} tone="secondary" onClick={props.onCancel}>Cancel</Button>
          <Button busy={props.busy} disabled={props.busy} tone="danger" onClick={props.onConfirm}>{props.busy ? 'Working…' : props.confirmLabel}</Button>
        </div>
      </div>
    </Sheet>
  );
}

export function FormStack(props: { children: ReactNode }) {
  return <div className="mira-form-stack">{props.children}</div>;
}

export function FormRow(props: { children: ReactNode; className?: string; icon?: string; label: string; meta?: string; required?: boolean }) {
  return (
    <label className={props.className}>
      {props.icon ? <span aria-hidden="true"><IconGlyph value={props.icon} /></span> : null}
      <div>
        <strong>{props.label}{props.required ? ' *' : ''}</strong>
        <small>{props.meta || (props.required ? 'Required' : 'Optional')}</small>
      </div>
      {props.children}
    </label>
  );
}

export function ActionDock(props: { children: ReactNode }) {
  return <div className="mira-action-dock">{props.children}</div>;
}

function ScreenFrame(props: { title: string; eyebrow?: string; kind: string; children: ReactNode }) {
  return (
    <article className="mira-screen" data-kind={props.kind}>
      <header className="mira-screen-header">
        {props.eyebrow ? <p>{props.eyebrow}</p> : null}
        <h1>{props.title}</h1>
      </header>
      <Card>{props.children}</Card>
    </article>
  );
}
